import { useState, useRef, useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useAppStore } from '../store/useAppStore';
import { getMainGod, getEventGods } from '../data/mockData';
import type { ChatMessage, GodName, ActionStatus, EventType } from '../types';
import { Button, Tag, Input } from 'animal-island-ui';
import { GOD_AVATARS, GOD_IMAGES } from '../godImages';
import GodAvatar from './GodAvatar';
import clsx from 'clsx';
import { chatStream } from '../lib/api';

// 当前时间 HH:MM
function nowTime(): string {
  return new Date().toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// YYYY-MM-DD -> YYYY年M月D日
function formatDate(s: string): string {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${y}年${Number(m)}月${Number(d)}日`;
}

// 金额格式化
function formatBudget(n: number): string {
  if (n >= 10000) {
    const w = n / 10000;
    return `${Number.isInteger(w) ? w : w.toFixed(1)}万`;
  }
  return `${n}元`;
}

// 高亮消息中把"喜事啊！"用 span 包裹，大红加粗
function renderMessageText(text: string, highlight?: boolean): ReactNode {
  if (!highlight) return text;
  const key = '喜事啊！';
  const idx = text.indexOf(key);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: '#e63946', fontWeight: 'bold' }}>{key}</span>
      {text.slice(idx + key.length)}
    </>
  );
}

// 动作卡内容描述
function describeAction(tool?: string, payload?: any): { title: string; lines: string[] } {
  if (!payload) return { title: '神仙提议', lines: [] };
  if (tool === 'add_ledger_entry') {
    const p = payload as { type?: string; category?: string; amount?: number; note?: string };
    const typeLabel = p.type === 'expense' ? '支出' : p.type === 'income' ? '收入' : '预算';
    const lines = [
      `类别：${p.category || '-'}`,
      `金额：¥${Number(p.amount || 0).toLocaleString()}`,
    ];
    if (p.note) lines.push(`备注：${p.note}`);
    return { title: `记一笔账 · ${typeLabel}`, lines };
  }
  if (tool === 'add_todo') {
    const p = payload as { title?: string; dueDate?: string; priority?: number };
    const lines = [`标题：${p.title || '-'}`];
    if (p.dueDate) lines.push(`截止：${String(p.dueDate).slice(0, 10)}`);
    return { title: '记一条待办', lines };
  }
  return { title: '神仙提议', lines: [JSON.stringify(payload)] };
}

const primaryBtnStyle: CSSProperties = {
  background: '#e63946',
  borderColor: '#e63946',
  boxShadow: '0 4px 0 #b02a37',
};

// 动作卡边框配色
const actionCardStyle: Record<ActionStatus, { border: string; bg: string; tag: string; tagBg: string }> = {
  pending: { border: '#f4a261', bg: '#fff8e1', tag: '待确认', tagBg: '#f4a261' },
  confirmed: { border: '#52b788', bg: '#f0fff4', tag: '已确认', tagBg: '#52b788' },
  cancelled: { border: '#bdbdbd', bg: '#f5f5f5', tag: '已取消', tagBg: '#bdbdbd' },
};

// 流式累加中的临时神仙回复
interface StreamingReply {
  godId: string;
  text: string;
}

export default function GroupChat() {
  const {
    username,
    currentProjectId,
    projects,
    messages,
    addMessage,
    gods,
    chatGuideStep,
    setChatGuideStep,
    updateProjectDate,
    updateProjectBudget,
    countdownUpdated,
    budgetUpdated,
    setCountdownUpdated,
    setBudgetUpdated,
    loadMessages,
    resolveAction,
  } = useAppStore();

  const [input, setInput] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [budgetValue, setBudgetValue] = useState('');
  const [streaming, setStreaming] = useState<StreamingReply[]>([]);
  const [sending, setSending] = useState(false);
  const [mentions, setMentions] = useState<string[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  // 记录已添加的引导消息 id，防止重复添加
  const addedMsgsRef = useRef<Set<number>>(new Set());
  // 历史是否已就绪（加载完成后才允许驱动引导流程，避免引导消息覆盖真实历史）
  const [historyReady, setHistoryReady] = useState(false);
  const prevProjectRef = useRef<number | null>(currentProjectId);

  const project = projects.find((p) => p.id === currentProjectId);
  const msgList = currentProjectId ? messages[currentProjectId] || [] : [];
  const groupGods: GodName[] = project ? deriveEventGods(project.type) : [];
  const groupGodObjs = groupGods.map((g) => gods[g]).filter(Boolean);

  // 切换项目时重置引导消息记录与历史就绪标记
  if (prevProjectRef.current !== currentProjectId) {
    prevProjectRef.current = currentProjectId;
    addedMsgsRef.current = new Set();
  }

  // 进入项目时拉历史消息，加载完成后再决定引导/自由聊天
  useEffect(() => {
    if (!currentProjectId) return;
    setHistoryReady(false);
    let cancelled = false;
    loadMessages(currentProjectId).then(() => {
      if (cancelled) return;
      const existing = useAppStore.getState().messages[currentProjectId] || [];
      const step = useAppStore.getState().chatGuideStep;
      // 有历史消息则直接进入自由聊天；否则启动引导
      if (existing.length > 0) {
        if (step !== 'free-chat') setChatGuideStep('free-chat');
      } else if (step === 'free-chat') {
        setChatGuideStep('xishen-announce');
      }
      setHistoryReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, loadMessages, setChatGuideStep]);

  // 群聊引导流程：由 chatGuideStep 驱动（引导阶段不调 LLM，固定话术）
  useEffect(() => {
    if (!currentProjectId || !project || !historyReady) return;
    const pid = currentProjectId;
    const step = chatGuideStep;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const addGuarded = (msg: ChatMessage) => {
      if (!addedMsgsRef.current.has(msg.id)) {
        addedMsgsRef.current.add(msg.id);
        addMessage(pid, msg);
      }
    };

    if (step === 'xishen-announce') {
      const existing = useAppStore.getState().messages[pid] || [];
      if (existing.length > 0) {
        timers.push(setTimeout(() => setChatGuideStep('free-chat'), 0));
      } else {
        const godNames = deriveEventGods(project.type).map((g) => gods[g]?.name || g).join('、');
        addGuarded({
          id: -1,
          god: 'xishen',
          kind: 'text',
          text: `@all，${username}即将迎来${project.title}，我拉了${godNames}一起帮你筹办好你的大事。`,
          timestamp: nowTime(),
        });
        timers.push(setTimeout(() => setChatGuideStep('gods-greet'), 1500));
      }
    } else if (step === 'gods-greet') {
      deriveEventGods(project.type).forEach((g, i) => {
        timers.push(
          setTimeout(() => {
            addGuarded({
              id: -(i + 2),
              god: g,
              kind: 'text',
              text: `喜事啊！`,
              timestamp: nowTime(),
              highlight: true,
            });
          }, (i + 1) * 1000),
        );
      });
      timers.push(
        setTimeout(() => setChatGuideStep('ask-date'), (deriveEventGods(project.type).length + 1) * 1000),
      );
    } else if (step === 'ask-date') {
      addGuarded({
        id: -100,
        god: getMainGod(project.type),
        kind: 'text',
        text: '喜事什么时候呢？',
        timestamp: nowTime(),
      });
    } else if (step === 'date-confirmed') {
      timers.push(setTimeout(() => setChatGuideStep('ask-budget'), 1200));
    } else if (step === 'ask-budget') {
      addGuarded({
        id: -101,
        god: 'caishen',
        kind: 'text',
        text: '办这喜事预算多少啊？',
        timestamp: nowTime(),
      });
    } else if (step === 'budget-confirmed') {
      // 引导结束，直接进入自由聊天（用户已登录建项，无需再提示注册）
      timers.push(setTimeout(() => setChatGuideStep('free-chat'), 1200));
    }

    return () => timers.forEach(clearTimeout);
  }, [chatGuideStep, currentProjectId, project, username, gods, historyReady, addMessage, setChatGuideStep]);

  // 自动滚动到最新消息
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgList, streaming]);

  // 3秒后淡出已更新标记
  useEffect(() => {
    if (countdownUpdated) {
      const timer = setTimeout(() => setCountdownUpdated(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [countdownUpdated, setCountdownUpdated]);

  useEffect(() => {
    if (budgetUpdated) {
      const timer = setTimeout(() => setBudgetUpdated(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [budgetUpdated, setBudgetUpdated]);

  const handleConfirmDate = async () => {
    if (!dateValue || !currentProjectId) return;
    addMessage(currentProjectId, {
      id: -Date.now(),
      god: 'user',
      kind: 'text',
      text: formatDate(dateValue),
      timestamp: nowTime(),
    });
    await updateProjectDate(currentProjectId, dateValue);
    setChatGuideStep('date-confirmed');
  };

  const handleConfirmBudget = async () => {
    const n = Number(budgetValue);
    if (!n || !currentProjectId) return;
    addMessage(currentProjectId, {
      id: -Date.now(),
      god: 'user',
      kind: 'text',
      text: formatBudget(n),
      timestamp: nowTime(),
    });
    await updateProjectBudget(currentProjectId, n);
    setChatGuideStep('budget-confirmed');
  };

  // 切换 @ 召唤的神仙
  const toggleMention = (godId: string) => {
    setMentions((prev) =>
      prev.includes(godId) ? prev.filter((g) => g !== godId) : [...prev, godId],
    );
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !currentProjectId || sending) return;
    // 乐观追加用户消息（后端已落库，本地用临时负 id 避免与真实 id 冲突）
    addMessage(currentProjectId, {
      id: -Date.now(),
      god: 'user',
      kind: 'text',
      text,
      timestamp: nowTime(),
    });
    setInput('');
    setSending(true);
    setStreaming([]);

    const pid = currentProjectId;
    try {
      await chatStream(pid, text, mentions, {
        onDelta: (godId, delta) => {
          setStreaming((prev) => {
            const idx = prev.findIndex((s) => s.godId === godId);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = { godId, text: next[idx].text + delta };
              return next;
            }
            return [...prev, { godId, text: delta }];
          });
        },
        onDone: (reply) => {
          // 清除该神仙的流式临时消息，落正式消息
          setStreaming((prev) => prev.filter((s) => s.godId !== reply.godId));
          addMessage(pid, {
            id: reply.messageId,
            god: reply.godId as GodName,
            kind: reply.action ? 'action' : 'text',
            text: reply.content,
            timestamp: nowTime(),
            actionTool: reply.action?.tool,
            actionPayload: reply.action?.payload,
            actionStatus: reply.action ? 'pending' : undefined,
          });
        },
        onError: () => {
          setStreaming([]);
        },
      });
    } catch {
      setStreaming([]);
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async (messageId: number, status: ActionStatus) => {
    if (!currentProjectId) return;
    await resolveAction(messageId, status);
  };

  if (!project || !currentProjectId) {
    return (
      <div
        className="flex items-center justify-center h-full text-gray-400"
        style={{ minWidth: 320 }}
      >
        请先选择一个喜事项目
      </div>
    );
  }

  const groupName = `天庭有关${username || '你'}喜事的筹办委员会`;
  const todayStr = new Date().toISOString().slice(0, 10);
  const canFreeChat = chatGuideStep === 'free-chat';

  return (
    <div className="flex flex-col h-full" style={{ minWidth: 320, background: '#fdf6e3' }}>
      {/* 群聊头部 */}
      <div
        id="groupchat-header"
        className="flex items-center gap-3 px-4 py-3 bg-white border-b-2 border-amber-100"
      >
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-sm text-gray-800 truncate">{groupName}</h2>
          <p className="text-xs text-gray-400 truncate">
            群成员 {groupGodObjs.length + 1} 人
          </p>
        </div>
      </div>

      {/* 群成员栏 - 悬停显示完整图片 */}
      <div
        id="groupchat-members"
        className="flex flex-wrap items-center gap-3 px-4 py-2 bg-amber-50/50 border-b border-amber-100 relative z-20"
      >
        <span className="text-xs text-gray-400 whitespace-nowrap">群成员：</span>
        {groupGodObjs.map((g) => (
          <div key={g.id} className="relative flex items-center gap-1 whitespace-nowrap group cursor-pointer">
            <img src={GOD_AVATARS[g.id]} alt={g.name} className="w-5 h-5 rounded-full object-cover" />
            <span className="text-xs font-medium" style={{ color: g.color }}>
              {g.name}
            </span>
            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              <img
                src={GOD_IMAGES[g.id]}
                alt={g.name}
                className="w-32 h-32 object-contain rounded-lg shadow-lg bg-white border-2 border-amber-200"
              />
            </div>
          </div>
        ))}
        <span className="text-xs text-gray-300">|</span>
        <span className="text-base">🧑</span>
        <span className="text-xs font-medium text-gray-600">{username || '你'}</span>
      </div>

      {/* 消息列表 */}
      <div
        id="groupchat-messages"
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      >
        {/* 系统消息 */}
        <div className="flex justify-center">
          <span className="text-xs text-gray-400 bg-amber-50 px-3 py-1 rounded-full">
            喜神创建了群聊，开始为您筹办喜事
          </span>
        </div>

        {!historyReady && (
          <div className="flex justify-center">
            <span className="text-xs text-gray-400">加载历史消息...</span>
          </div>
        )}

        {msgList.map((msg) => {
          if (msg.god === 'user') {
            return (
              <div key={msg.id} className="flex justify-end bounce-in">
                <div className="flex items-end gap-2 max-w-[75%]">
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-400 mb-0.5 mr-1">{msg.timestamp}</span>
                    <div className="chat-bubble text-sm" style={{ background: '#e63946', color: 'white' }}>
                      {msg.text}
                    </div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center text-lg flex-shrink-0">
                    🧑
                  </div>
                </div>
              </div>
            );
          }

          if (msg.god === 'system') {
            return (
              <div key={msg.id} className="flex justify-center">
                <span className="text-xs text-gray-400 bg-amber-50 px-3 py-1 rounded-full">
                  {msg.text}
                </span>
              </div>
            );
          }

          const god = gods[msg.god];
          if (!god) return null;
          const isAction = msg.kind === 'action';
          const status = msg.actionStatus || 'pending';
          const cardStyle = actionCardStyle[status];
          const desc = isAction ? describeAction(msg.actionTool, msg.actionPayload) : null;

          return (
            <div key={msg.id} className="flex justify-start bounce-in">
              <div className="flex items-start gap-2 max-w-[75%]">
                <GodAvatar god={msg.god} size={36} className="mt-4 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-1.5 ml-1">
                    <span className="text-xs font-medium" style={{ color: god.color }}>
                      {god.name}
                    </span>
                    <span className="text-xs text-gray-400">{msg.timestamp}</span>
                  </div>
                  <div
                    className="chat-bubble text-sm mt-0.5"
                    style={{ background: god.bgColor, color: '#333', border: `1px solid ${god.color}30` }}
                  >
                    {renderMessageText(msg.text, msg.highlight)}
                  </div>
                  {/* 动作卡 */}
                  {isAction && desc && (
                    <div
                      className="mt-1.5 rounded-xl p-3 max-w-xs"
                      style={{ background: cardStyle.bg, border: `2px solid ${cardStyle.border}` }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold" style={{ color: cardStyle.border }}>
                          {desc.title}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full text-white"
                          style={{ background: cardStyle.tagBg }}
                        >
                          {cardStyle.tag}
                        </span>
                      </div>
                      <div className="space-y-0.5 mb-2">
                        {desc.lines.map((line, i) => (
                          <p key={i} className="text-xs text-gray-600">{line}</p>
                        ))}
                      </div>
                      {status === 'pending' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleResolve(msg.id, 'confirmed')}
                            className="flex-1 text-xs py-1.5 rounded-lg text-white font-medium"
                            style={{ background: '#52b788' }}
                          >
                            确认
                          </button>
                          <button
                            onClick={() => handleResolve(msg.id, 'cancelled')}
                            className="flex-1 text-xs py-1.5 rounded-lg text-gray-600 font-medium bg-white border border-gray-300"
                          >
                            取消
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* 流式正在输入的神仙回复 */}
        {streaming.map((s) => {
          const god = gods[s.godId];
          if (!god) return null;
          return (
            <div key={s.godId} className="flex justify-start bounce-in">
              <div className="flex items-start gap-2 max-w-[75%]">
                <GodAvatar god={s.godId as GodName} size={36} className="mt-4 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-1.5 ml-1">
                    <span className="text-xs font-medium" style={{ color: god.color }}>
                      {god.name}
                    </span>
                    <span className="text-xs text-gray-400">正在输入...</span>
                  </div>
                  <div
                    className="chat-bubble text-sm mt-0.5"
                    style={{ background: god.bgColor, color: '#333', border: `1px solid ${god.color}30` }}
                  >
                    {s.text}
                    <span className="typing-cursor" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 输入区 */}
      <div id="groupchat-input" className="px-4 py-3 bg-white border-t-2 border-amber-100">
        {chatGuideStep === 'ask-date' ? (
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmDate()}
              min={todayStr}
              className="flex-1 px-4 py-3 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50 text-sm"
            />
            <Button
              type="primary"
              danger
              disabled={!dateValue}
              onClick={handleConfirmDate}
              style={primaryBtnStyle}
              className={clsx('transition-opacity', dateValue ? 'hover:opacity-90' : 'opacity-50')}
            >
              确认
            </Button>
          </div>
        ) : chatGuideStep === 'ask-budget' ? (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <input
                type="number"
                value={budgetValue}
                onChange={(e) => setBudgetValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirmBudget()}
                placeholder="请输入金额（元）"
                className="flex-1 px-4 py-3 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50 text-sm"
              />
              <Button
                type="primary"
                danger
                disabled={!budgetValue || !Number(budgetValue)}
                onClick={handleConfirmBudget}
                style={primaryBtnStyle}
                className={clsx(
                  'transition-opacity',
                  budgetValue && Number(budgetValue) ? 'hover:opacity-90' : 'opacity-50',
                )}
              >
                确认
              </Button>
            </div>
            <span className="text-xs text-gray-400">预算</span>
          </div>
        ) : canFreeChat ? (
          <>
            {/* @ 召唤栏 */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <span className="text-xs text-gray-400">@召唤：</span>
              {groupGodObjs.map((g) => {
                const selected = mentions.includes(g.id);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleMention(g.id)}
                    className={clsx(
                      'flex items-center gap-1 px-2 py-1 rounded-full text-xs border-2 transition-all',
                      selected
                        ? 'text-white'
                        : 'bg-amber-50 text-gray-600 border-amber-200 hover:border-amber-400',
                    )}
                    style={selected ? { background: g.color, borderColor: g.color } : undefined}
                  >
                    <img src={GOD_AVATARS[g.id]} alt={g.name} className="w-4 h-4 rounded-full object-cover" />
                    {g.name}
                  </button>
                );
              })}
              <span className="text-xs text-gray-300">（不选则主理神仙接话）</span>
            </div>
            {/* 建议问题 */}
            <div className="flex gap-2 mb-2">
              {['预算多少？', '帮我排个日程', '采购清单好了吗？'].map((suggestion) => (
                <Tag
                  key={suggestion}
                  size="small"
                  onClick={() => setInput(suggestion)}
                >
                  {suggestion}
                </Tag>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <Input
                size="large"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder={sending ? '神仙回复中...' : '说点什么...'}
                disabled={sending}
                className="flex-1"
              />
              <Button
                type="primary"
                danger
                disabled={!input.trim() || sending}
                onClick={handleSend}
                style={primaryBtnStyle}
                className={clsx('transition-opacity', input.trim() && !sending ? 'hover:opacity-90' : 'opacity-50')}
              >
                发送
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              size="large"
              disabled
              placeholder="诸神正在为您筹办中..."
              className="flex-1"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// 派生某喜事的群神仙组合（喜神 + 该类型主管神组合）
function deriveEventGods(type: EventType): GodName[] {
  return ['xishen', ...getEventGods(type)];
}
